// ---------------------------------------------------------------------------
// Centralized color token system for tio-reimagined components
//
// Instead of each component defining its own `colorClasses` record, all
// color tokens live here. Components import only the helpers they need.
// ---------------------------------------------------------------------------

/**
 * Superset of all color names used across reimagined components.
 * Individual components may accept a narrower subset via their own type alias.
 */
export type ReimaginedColor =
  | 'emerald'
  | 'blue'
  | 'violet'
  | 'amber'
  | 'teal'
  | 'cyan'
  | 'red'
  | 'green'
  | 'indigo'
  | 'purple'
  | 'sky'
  | 'pink'
  | 'rose'
  | 'gray'
  | 'orange';

// ---------------------------------------------------------------------------
// Icon tokens — moved to the .app-icon-badge* semantic class family in
// addons/tio-reimagined/src/styles.css. The 15-color decorative palette
// is preserved at each component's API but collapses onto the 6 canonical
// TIO recipes (neutral / success / info / brand / danger / warning) via
// CSS modifiers. Consumers (HighlightCard, FeatureCard, StatCard,
// SectionPanel, etc.) compose against .app-icon-badge directly.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Value text color — replaced by call-site application of the canonical
// status/recipe text utilities (text-[var(--success-icon)] etc.) and the
// .app-stat-card-value default in addons/tio-reimagined/src/styles.css.
// StatCard maps its @valueColor decorative palette onto those recipes
// locally via a small recipeFor() helper.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Icon-only text color — moved to the .app-section-panel* semantic
// class family in addons/tio-reimagined/src/styles.css. The 15-color
// decorative palette is preserved at the API level (SectionPanelIconColor
// in section-panel.gts) but collapses onto the 6 canonical TIO recipes
// (neutral / success / info / brand / danger / warning) via CSS
// modifiers.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Gradient tokens — moved to the .app-highlight-card* semantic class
// family in addons/tio-reimagined/src/styles.css. The 8-color decorative
// palette is preserved at the API level (HighlightCardColor in
// highlight-card.gts) but collapses onto the 5 canonical TIO recipes
// (success / info / brand / warning / danger) via CSS modifiers.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Nav-card specific tokens (hover effects, gradient variant)
// ---------------------------------------------------------------------------

export interface NavColorTokens {
  bg: string;
  text: string;
  hoverBg: string;
  hoverBorder: string;
  hoverCaret: string;
  gradient: string;
  gradientBorder: string;
}

const navTokens: Record<string, NavColorTokens> = {
  violet: {
    bg: 'bg-violet-100 dark:bg-violet-900/50',
    text: 'text-violet-600 dark:text-violet-400',
    hoverBg: 'group-hover:bg-violet-600 group-hover:text-white',
    hoverBorder: 'hover:ring-violet-300 dark:hover:ring-violet-600',
    hoverCaret: 'group-hover:text-violet-600 dark:group-hover:text-violet-400',
    gradient: 'from-violet-50 to-purple-50 dark:from-violet-900/30 dark:to-purple-900/30',
    gradientBorder: 'border-violet-100 dark:border-violet-800',
  },
  indigo: {
    bg: 'bg-indigo-100 dark:bg-indigo-900/50',
    text: 'text-indigo-600 dark:text-indigo-400',
    hoverBg: 'group-hover:bg-indigo-600 group-hover:text-white',
    hoverBorder: 'hover:ring-indigo-300 dark:hover:ring-indigo-600',
    hoverCaret: 'group-hover:text-indigo-600 dark:group-hover:text-indigo-400',
    gradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30',
    gradientBorder: 'border-indigo-100 dark:border-indigo-800',
  },
  teal: {
    bg: 'bg-teal-100 dark:bg-teal-900/50',
    text: 'text-teal-600 dark:text-teal-400',
    hoverBg: 'group-hover:bg-teal-600 group-hover:text-white',
    hoverBorder: 'hover:ring-teal-300 dark:hover:ring-teal-600',
    hoverCaret: 'group-hover:text-teal-600 dark:group-hover:text-teal-400',
    gradient: 'from-teal-50 to-cyan-50 dark:from-teal-900/30 dark:to-cyan-900/30',
    gradientBorder: 'border-teal-100 dark:border-teal-800',
  },
  emerald: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/50',
    text: 'text-emerald-600 dark:text-emerald-400',
    hoverBg: 'group-hover:bg-emerald-600 group-hover:text-white',
    hoverBorder: 'hover:ring-emerald-300 dark:hover:ring-emerald-600',
    hoverCaret: 'group-hover:text-emerald-600 dark:group-hover:text-emerald-400',
    gradient: 'from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30',
    gradientBorder: 'border-emerald-100 dark:border-emerald-800',
  },
  blue: {
    bg: 'bg-blue-100 dark:bg-blue-900/50',
    text: 'text-blue-600 dark:text-blue-400',
    hoverBg: 'group-hover:bg-blue-600 group-hover:text-white',
    hoverBorder: 'hover:ring-blue-300 dark:hover:ring-blue-600',
    hoverCaret: 'group-hover:text-blue-600 dark:group-hover:text-blue-400',
    gradient: 'from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30',
    gradientBorder: 'border-blue-100 dark:border-blue-800',
  },
  amber: {
    bg: 'bg-amber-100 dark:bg-amber-900/50',
    text: 'text-amber-600 dark:text-amber-400',
    hoverBg: 'group-hover:bg-amber-600 group-hover:text-white',
    hoverBorder: 'hover:ring-amber-300 dark:hover:ring-amber-600',
    hoverCaret: 'group-hover:text-amber-600 dark:group-hover:text-amber-400',
    gradient: 'from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30',
    gradientBorder: 'border-amber-100 dark:border-amber-800',
  },
  pink: {
    bg: 'bg-pink-100 dark:bg-pink-900/50',
    text: 'text-pink-600 dark:text-pink-400',
    hoverBg: 'group-hover:bg-pink-600 group-hover:text-white',
    hoverBorder: 'hover:ring-pink-300 dark:hover:ring-pink-600',
    hoverCaret: 'group-hover:text-pink-600 dark:group-hover:text-pink-400',
    gradient: 'from-pink-50 to-rose-50 dark:from-pink-900/30 dark:to-rose-900/30',
    gradientBorder: 'border-pink-100 dark:border-pink-800',
  },
  green: {
    bg: 'bg-green-100 dark:bg-green-900/50',
    text: 'text-green-600 dark:text-green-400',
    hoverBg: 'group-hover:bg-green-600 group-hover:text-white',
    hoverBorder: 'hover:ring-green-300 dark:hover:ring-green-600',
    hoverCaret: 'group-hover:text-green-600 dark:group-hover:text-green-400',
    gradient: 'from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30',
    gradientBorder: 'border-green-100 dark:border-green-800',
  },
  rose: {
    bg: 'bg-rose-100 dark:bg-rose-900/50',
    text: 'text-rose-600 dark:text-rose-400',
    hoverBg: 'group-hover:bg-rose-600 group-hover:text-white',
    hoverBorder: 'hover:ring-rose-300 dark:hover:ring-rose-600',
    hoverCaret: 'group-hover:text-rose-600 dark:group-hover:text-rose-400',
    gradient: 'from-rose-50 to-pink-50 dark:from-rose-900/30 dark:to-pink-900/30',
    gradientBorder: 'border-rose-100 dark:border-rose-800',
  },
};

export function getNavTokens(color?: string): NavColorTokens {
  return navTokens[color ?? 'violet'] ?? navTokens.violet!;
}

// ---------------------------------------------------------------------------
// CTA banner text tokens
// ---------------------------------------------------------------------------

interface CtaTextTokens {
  gradient: string;
  heading: string;
  description: string;
}

const ctaTokens: Record<string, CtaTextTokens> = {
  emerald: {
    gradient:
      'bg-linear-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20',
    heading: 'text-emerald-900 dark:text-emerald-100',
    description: 'text-emerald-700 dark:text-emerald-300',
  },
  teal: {
    gradient: 'bg-linear-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20',
    heading: 'text-teal-900 dark:text-teal-100',
    description: 'text-teal-700 dark:text-teal-300',
  },
  blue: {
    gradient:
      'bg-linear-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20',
    heading: 'text-blue-900 dark:text-blue-100',
    description: 'text-blue-700 dark:text-blue-300',
  },
  violet: {
    gradient:
      'bg-linear-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20',
    heading: 'text-violet-900 dark:text-violet-100',
    description: 'text-violet-700 dark:text-violet-300',
  },
  amber: {
    gradient:
      'bg-linear-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20',
    heading: 'text-amber-900 dark:text-amber-100',
    description: 'text-amber-700 dark:text-amber-300',
  },
  rose: {
    gradient: 'bg-linear-to-r from-rose-50 to-pink-50 dark:from-rose-900/20 dark:to-pink-900/20',
    heading: 'text-rose-900 dark:text-rose-100',
    description: 'text-rose-700 dark:text-rose-300',
  },
  indigo: {
    gradient:
      'bg-linear-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
    heading: 'text-indigo-900 dark:text-indigo-100',
    description: 'text-indigo-700 dark:text-indigo-300',
  },
  pink: {
    gradient: 'bg-linear-to-r from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20',
    heading: 'text-pink-900 dark:text-pink-100',
    description: 'text-pink-700 dark:text-pink-300',
  },
};

export function getCtaTokens(theme?: string) {
  return ctaTokens[theme ?? 'violet'] ?? ctaTokens.violet!;
}

// ---------------------------------------------------------------------------
// Empty-state icon tokens — moved to the .app-empty-state* semantic
// class family in styles.css. The decorative color names (gray /
// violet / blue / teal / amber / emerald) now resolve to TIO recipes
// via the --empty-bg + --empty-fg custom properties set by the color
// modifier class on the root element.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Icon badge tokens — moved to the .app-icon-badge* semantic class
// family in addons/tio-reimagined/src/styles.css. The 15-color
// decorative palette is preserved at the API level (IconBadgeColor in
// icon-badge.gts) but collapses onto the 6 canonical TIO recipes
// (neutral / success / info / brand / danger / warning) via CSS
// modifiers.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Toast intent — public type kept here for back-compat. The visual
// recipe (border / bg / icon / progress) moved to the `.app-toast*`
// semantic class family in styles.css, which routes through the
// canonical --success/danger/warning/info-* TIO tokens. The old
// getToastTokens() / ToastTokens interface were removed when no
// external consumer was found (only toast.gts itself used them).
// ---------------------------------------------------------------------------

export type ToastIntent = 'success' | 'error' | 'warning' | 'info';

// ---------------------------------------------------------------------------
// Confirmation dialog tokens — moved to the .app-confirmation* semantic
// class family in styles.css, which routes through the canonical
// --warning/danger/success/info-* TIO recipes. The ConfirmationIntent
// type now lives in confirmation-dialog.gts itself (only consumer of
// the old token system).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Popover intent — the rest of the tokens (bg / border / shadow / accent)
// moved to the .app-popover* semantic class family in
// addons/tio-reimagined/src/styles.css. The PopoverIntent union stays
// here so consumer components and tests can still import the type.
// ---------------------------------------------------------------------------

export type PopoverIntent = 'default' | 'info' | 'success' | 'warning' | 'danger';
